/**
 * Custom window events the wiki slash menu dispatches to ask the doc
 * page surface to open one of the Phase 4c embed flows. Same pattern
 * as `OPEN_LINK_POPOVER_EVENT` — the slash command itself can't host
 * the picker/upload modal (it has no React tree access from inside
 * the suggestion plugin's `command` callback), so it fires an event
 * and `<DocPage>` listens.
 *
 * Each event carries enough context to:
 *   1. Mount the picker / upload modal anchored against the caret.
 *   2. On commit, locate the freshly inserted embed node via its
 *      `pos` payload and call `editor.commands.updateAttributes` at
 *      that position to fill the attrs.
 *   3. On cancel, delete the placeholder node at `pos` so an
 *      abandoned pick doesn't leave a "Pick a … " chip in the doc.
 *
 * The `pos` is captured by the slash command immediately after the
 * placeholder insert, so the DocPage doesn't have to search for the
 * node. Concurrent edits between insert and commit are vanishingly
 * rare in single-writer mode (Phase 4 stays single-writer; Yjs is
 * deferred), but the DocPage's commit handler is defensive — it
 * checks that the node at `pos` is still the expected type before
 * applying attrs.
 */

export const OPEN_WORK_ITEM_PICKER_EVENT = 'wiki:open-work-item-picker'
export const OPEN_VIDEO_UPLOAD_EVENT = 'wiki:open-video-upload'
export const OPEN_FILE_UPLOAD_EVENT = 'wiki:open-file-upload'
export const OPEN_IMAGE_UPLOAD_EVENT = 'wiki:open-image-upload'
/**
 * Wiki Image NodeView click → DocPage-level orchestrator opens the
 * lightbox carousel across every image in the open doc. Carries the
 * clicked image's identity so the orchestrator can seed `openIndex` at
 * the right slot.
 */
export const OPEN_IMAGE_LIGHTBOX_EVENT = 'wiki:open-image-lightbox'
/**
 * Wiki File NodeView click (chip body) → DocPage-level orchestrator
 * opens the file lightbox. Single-attachment (no carousel — files are
 * individual entries, not browsable like images). The lightbox branches
 * on MIME: PDFs render inline via `<iframe>`,
 * images via `<img>`, everything else as a download-only card with a
 * "no preview available" body. A prominent Download button is always
 * exposed regardless of branch.
 */
export const OPEN_FILE_LIGHTBOX_EVENT = 'wiki:open-file-lightbox'
/**
 * Cancel button on the in-flight image upload NodeView → orchestrator
 * aborts the FU-D PUT, removes the optimistic node from the editor,
 * revokes the blob URL, and best-effort `DELETE /api/uploads/:id`s the
 * attachment row. Keyed on the blob URL because that's the only stable
 * identifier the orchestrator has BEFORE the presign round-trip returns
 * an `attachmentId`.
 */
export const CANCEL_IMAGE_UPLOAD_EVENT = 'wiki:cancel-image-upload'
/**
 * OS-file drop on the wiki doc pane → orchestrator uploads. The doc
 * page's drop hook validates the batch (count cap, per-file size),
 * normalizes the caret (focuses the editor end when the user never
 * focused it this session), then dispatches with the accepted `File[]`.
 * The orchestrator routes each file by MIME (image node / video embed /
 * file chip), inserts uploading placeholders at the selection, and runs
 * the presign → S3 PUT pipeline per file. No positions ride the event —
 * they'd be stale by handler time; the selection is the contract.
 */
export const UPLOAD_DROPPED_FILES_EVENT = 'wiki:upload-dropped-files'
/**
 * DocPage → orchestrator: tear down EVERY in-flight upload (abort the
 * controllers, revoke blob URLs, remove uploading placeholders,
 * best-effort `DELETE /api/uploads/:id` any presigned rows). Dispatched
 * from the Cancel-discard and lock-lost exit paths, where the edit
 * session's content is being reverted and a late-resolving embed would
 * land in a buffer nobody keeps. No detail payload.
 */
export const ABORT_ALL_UPLOADS_EVENT = 'wiki:abort-all-uploads'
/**
 * Cancel button on an in-flight video / file uploading placeholder →
 * orchestrator aborts that upload, removes the placeholder node, and
 * best-effort deletes the presigned attachment row. The video / file
 * sibling of `CANCEL_IMAGE_UPLOAD_EVENT` — keyed on the transient
 * `uploadingPlaceholderId` attr instead of a blob URL (these flows
 * render no local preview, so there's no blob to key on).
 */
export const CANCEL_EMBED_UPLOAD_EVENT = 'wiki:cancel-embed-upload'

export interface OpenEmbedDetail {
  /**
   * Position of the freshly inserted placeholder node. The DocPage
   * uses this to update attrs (work-item / video / file) or to
   * delete the placeholder on cancel.
   *
   * For `/image` the placeholder isn't a node — TipTap's `Image`
   * extension is committed directly via `setImage({src, alt})` on
   * upload. `pos` may be omitted; the image extension inserts at the
   * current selection.
   */
  pos?: number
  /**
   * Anchor rect (caret position) the popover / modal floats against.
   * For full-screen modals (video / file uploads) this is informational
   * — the modal centers itself.
   */
  anchorRect?: { left: number; top: number; right: number; bottom: number }
}

/**
 * Detail payload for `OPEN_IMAGE_LIGHTBOX_EVENT`. Exactly one of
 * `attachmentId` / `src` is set per click:
 *   - `attachmentId` for Phase-1 uploads (NodeView resolves the signed
 *     GET URL just-in-time via `useImagePresignedUrl`).
 *   - `src` for legacy markdown docs that embed a remote URL directly.
 */
export interface OpenImageLightboxDetail {
  attachmentId: string | null
  src: string | null
  alt: string
}

/**
 * Detail payload for `CANCEL_IMAGE_UPLOAD_EVENT`. The blob URL is the
 * key the orchestrator uses to find the in-flight upload's
 * AbortController + attachment id (when the presign already returned).
 */
export interface CancelImageUploadDetail {
  uploadingBlobUrl: string
}

/**
 * Detail payload for `UPLOAD_DROPPED_FILES_EVENT`. `File` objects ride
 * the CustomEvent by reference — no serialization happens, so the
 * orchestrator streams the same handles the drop produced.
 */
export interface UploadDroppedFilesDetail {
  files: File[]
}

/**
 * Detail payload for `CANCEL_EMBED_UPLOAD_EVENT`. The placeholder id is
 * the key the orchestrator uses to find the in-flight upload's
 * AbortController + attachment id (when the presign already returned),
 * and the identity the placeholder helpers locate the node by.
 */
export interface CancelEmbedUploadDetail {
  uploadingPlaceholderId: string
}

/**
 * Retry a FAILED embed upload (error-state placeholder). Dispatched by
 * the NodeViews' Retry button; the orchestrator re-runs the network leg
 * against the SAME placeholder node using the `File` it kept in its
 * failed-uploads map. `key` is the placeholder identity — the blob URL
 * for images, the transient `uploadingPlaceholderId` for video / file.
 */
export const RETRY_EMBED_UPLOAD_EVENT = 'wiki:retry-embed-upload'

/**
 * Dismiss a FAILED embed upload — removes the error-state placeholder
 * from the doc and forgets the retained `File`. Same `key` semantics as
 * the retry event.
 */
export const DISMISS_EMBED_UPLOAD_EVENT = 'wiki:dismiss-embed-upload'

/** Detail payload shared by the retry + dismiss events. */
export interface EmbedUploadActionDetail {
  key: string
}

/**
 * Detail payload for `OPEN_FILE_LIGHTBOX_EVENT`. The orchestrator uses
 * `attachmentId` to drive a `useFilePresignedUrl(attachmentId)` lookup
 * inside the lightbox; the other fields are display metadata the chip
 * already had in scope and surface in the lightbox chrome (title,
 * "PDF · 2.3 MB" subline, icon picker) without a second BE round-trip.
 */
export interface OpenFileLightboxDetail {
  attachmentId: string
  fileName: string
  sizeBytes: number
  mimeType: string
}
